import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import { Controller, set, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import Styles from "../styles";
import ModalWithChildren from "../../../components/Shared/ModalWithChildren";
import ObjectSelector from "../../../components/ObjectSelector";
import PrimaryBtn from "components/Shared/Buttons/PrimaryBtn/PrimaryBtn";
import SecondaryBtn from "components/Shared/Buttons/SecondaryBtn/SecondaryBtn";
import usePreventEnterAction from "../../../hooks/PreventCloseOnEnterHook";
import { DashboardContext } from "../../../context/DashboardContext";

import { GET_VENUE, LIST_DOCKERS, LIST_MODELS } from "../../../graphql/graph";
import { GraphQLHelperContext } from "../../../context/GraphQLHelperContext";
import { jsonParse } from "../../../configs/helpers";
import Icon from "components/Shared/Icon/Icon";
import UseCaseGrid from "./UseCaseGrid/UseCaseGrid";
import useMachineData from "hooks/useMachineData";

// Step-based validation schema
/* eslint-disable indent */
const generateAddCameraSchema = (addNewArea) => {
  const commonSchema = {
    machineName: yup.string().required("Camera name is a required field."),
    machineRTSPUrl: yup.string().required("Camera Url is a required field."),
    machineCustomId: yup.string().typeError("Custom Id must be a number.").notRequired(),
    machineModel: yup.string().required("Model type is a required field."),
    modelVersionId: yup.string().notRequired(),
    dockerId: yup.string().required("Please provide a Docker Id"),
    machineAsyncInference: yup.boolean(),
    machineConfidenceThreshold: yup
      .number()
      .min(0)
      .max(100)
      .required("Confidence threshold is required"),
    machineResetInterval: yup.number(),
    machineAntiLag: yup.boolean(),
    machinePrivacy: yup.boolean(),
  };

  const areaSchema = addNewArea
    ? {
        newArea: yup.string().required("New area name is required"),
      }
    : {
        areaId: yup.string().nullable().required("Please select an area"),
      };

  return yup.object().shape({ ...commonSchema, ...areaSchema });
};
/* eslint-enable indent */

const AddUpdateCamera = ({ handleModalForwardClick, showModal, setShowModal, cameraObj }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedUseCase, setSelectedUseCase] = useState(null);
  const [addNewArea, setAddNewArea] = useState(false); // Tracks if "Add New Area" button was clicked

  const isUpdate = cameraObj?.id ? true : false;

  const { companyId } = useParams();
  const { padmeModels, objectDetectionItems } = useContext(DashboardContext);
  const sortedPadmeModels = padmeModels.sort((a, b) => a.value.localeCompare(b.value));
  const { createMachine, updateMachine } = useContext(GraphQLHelperContext);

  const { data: modelData } = useQuery(LIST_MODELS, {
    context: "graph",
    variables: {
      companyId,
    },
  });

  const { data: dockersData } = useQuery(LIST_DOCKERS, {
    context: { clientName: "graph" },
    variables: {
      companyId,
    },
  });

  const [mlModels, setMlModels] = useState([]);
  const [sliderValue, setSliderValue] = useState(padmeModels[0].inferenceCPU.defaultValue);

  const [selectedModel, setSelectedModel] = useState(() => {
    if (!isUpdate) {
      return { ...padmeModels[0].inferenceCPU, type: padmeModels[0].type };
    }
    const model = padmeModels.find(({ value }) => value === cameraObj?.machineModel);
    const selectionObj = {
      cuda: { ...model.inferenceGPU, type: model.type },
    };
    return selectionObj[cameraObj?.machineType] || { ...model.inferenceCPU, type: model.type };
  });

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    clearErrors,
    formState: { errors },
    control,
  } = useForm({
    resolver: yupResolver(generateAddCameraSchema(addNewArea)),
    defaultValues: {
      machineName: "",
      machineRTSPUrl: "",
      machineCustomId: "",
      machineType: "cpu",
      machineOverlayLabel: "",
      machineInferenceInterval: padmeModels[0].inferenceCPU.defaultValue,
      machineSelectedObjects: [],
      machineIntervalType: "",
      modelVersionId: "",
      dockerId: "",
      areaId: null,
      areaName: null,
      machineModel: padmeModels.length && padmeModels[0].value,
      machineAsyncInference: false,
      machineConfidenceThreshold: 50,
      machineResetInterval: 30,
      machineAntiLag: false,
      machinePrivacy: false,
    },
  });
  const { buildCommonMachinePayload } = useMachineData();

  const watchAll = watch();

  useEffect(() => {
    if (cameraObj.id) {
      Object.entries(cameraObj).forEach(([key, value]) => {
        if (key === "machineSelectedObjects") {
          const parsedValue = typeof value === "string" ? jsonParse(value) : value;
          const normalizedValue = Array.isArray(parsedValue) ? parsedValue : [];
          setValue("machineSelectedObjects", normalizedValue);
        } else if (["machineAsyncInference", "machineAntiLag", "machinePrivacy"].includes(key)) {
          setValue(key, !!value);
        } else {
          setValue(key, value);
        }
      });
      setSelectedUseCase(cameraObj.machineModel);
    } else {
      setSelectedUseCase(padmeModels[0].value);
    }
  }, [cameraObj]);

  const handleSelectedModelUpdate = useCallback(() => {
    if (watchAll.machineType && watchAll.machineModel) {
      const model = padmeModels.find(({ value }) => value === watchAll.machineModel);

      const selectThisModel =
        watchAll.machineType === "cuda"
          ? { ...model.inferenceGPU, type: model.type }
          : { ...model.inferenceCPU, type: model.type };
      if (selectThisModel.key !== selectedModel.key) {
        setSelectedModel(selectThisModel);
        setValue("machineInferenceInterval", selectThisModel.defaultValue);
      }
    }
  }, [watchAll, setSelectedModel]);

  const objectSuggestions = objectDetectionItems
    .map((item) => ({
      value: item.objectDetectionIndex,
      label: item.objectDetectionLabel,
    }))
    .toSorted((a, b) => a.label.localeCompare(b.label));

  useEffect(() => {
    if (modelData?.listModel?.data && watchAll) {
      let newModelsList = [];
      const selectedModel = padmeModels.find((model) => model.value === watchAll.machineModel);

      if (selectedModel?.type === "density_estimation") {
        newModelsList = modelData.listModel.data.filter(
          (row) => row.modelType === selectedModel.type,
        );
      } else if (selectedModel?.type === "object_detection") {
        if (watchAll.machineSelectedObjects || watchAll.machineSelectedObjects.length) {
          newModelsList = modelData?.listModel?.data.filter((row) => {
            const isSelectedModel = row.modelType === selectedModel?.type;
            const rowModelObjects = jsonParse(row.modelDetectionObjects).map(
              (object) => object.label,
            );
            const selectedObjects = watchAll?.machineSelectedObjects?.map((object) => object.label);
            const hasSelectedObjectsInRow = selectedObjects.every((sel) =>
              rowModelObjects.includes(sel),
            );
            return isSelectedModel && hasSelectedObjectsInRow;
          });
        }
      }

      setMlModels(newModelsList);
      if (newModelsList.length) {
        setTimeout(() => {
          setValue("modelVersionId", watchAll.modelVersionId);
        }, 300);
      }
    }
  }, [modelData, watchAll.machineModel, setMlModels, watchAll.machineSelectedObjects]);

  useEffect(() => {
    handleSelectedModelUpdate();
  }, [watchAll.machineModel, watchAll.machineType]);

  useEffect(() => {
    const initialValue = cameraObj.machineInferenceInterval || selectedModel.defaultValue;
    const displayValue = selectedModel.valueType === "seconds" ? initialValue : initialValue * 100;

    if (cameraObj.id) {
      setValue(
        "machineInferenceInterval",
        cameraObj.machineInferenceInterval || selectedModel.defaultValue,
      );
      setSliderValue(displayValue);
    } else {
      setValue("machineInferenceInterval", selectedModel.defaultValue);
      setSliderValue(displayValue);
    }
  }, [cameraObj, selectedModel, setValue, setSliderValue]);

  const handleNextStep = (data) => {
    setCurrentStep((prev) => prev + 1);
  };

  const handlePrevStep = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const submitForm = (cameraFormData) => {
    // Check if the user is adding a new area or selecting an existing one
    const isAddingNewArea = addNewArea && cameraFormData.newArea;

    if (!cameraObj.id) {
      createMachine({
        variables: buildCommonMachinePayload(
          cameraObj,
          cameraFormData,
          selectedModel,
          isAddingNewArea,
        ),
        refetchQueries: [{ query: GET_VENUE, variables: { id: cameraObj.venueId } }],
      });
    } else {
      updateMachine({
        variables: {
          id: cameraObj.id,
          dockerId: cameraFormData.dockerId,
          ...buildCommonMachinePayload(cameraObj, cameraFormData, selectedModel, isAddingNewArea),
        },
      });
    }

    reset();
    setCurrentStep(1);
    setShowModal();
  };

  usePreventEnterAction(submitForm);

  const handleAreaOnChange = (e) => {
    setAddNewArea(false); // Clear "Add New Area" input when an area is selected
    clearErrors("areaId");
    clearErrors("newArea");
  };

  const handleObjectChange = (objects) => {
    setValue("machineSelectedObjects", objects);
  };

  const handleSliderChange = (value) => {
    const title = selectedModel.valueType === "seconds" ? value : `${value * 100}%`;
    setSliderValue(title);
  };

  const Track = (props, state) => <Styles.SliderTrack {...props} index={state.index} />;
  const Thumb = (props, state) => {
    return <Styles.SliderThumb {...props}></Styles.SliderThumb>;
  };

  const stepTitles = [
    { title: "Add Your Camera", subtitle: "STEP 1 OF 4" },
    { title: "Select a Use Case", subtitle: "STEP 2 OF 4" },
    { title: "Choose a Model", subtitle: "STEP 3 OF 4" },
    { title: "Last Details", subtitle: "STEP 4 OF 4" },
  ];

  const venueAreas = cameraObj?.venueAreas || [];
  const dockersList = dockersData?.listDocker?.data || [];

  const handleUseCaseSelect = (value) => {
    setSelectedUseCase(value);
    setValue("machineModel", value);
  };

  return (
    <ModalWithChildren
      showModal={showModal}
      setShowModal={setShowModal}
      title={stepTitles[currentStep - 1].title}
      subtitle={stepTitles[currentStep - 1].subtitle}
      handleModalForwardClick={handleModalForwardClick}
    >
      <Styles.Form wide onSubmit={handleSubmit(submitForm)}>
        <Styles.CameraFormContainer>
          {/* Step 1: Camera Details */}
          {currentStep === 1 && (
            <>
              <div style={{ alignSelf: "center", margin: "20px" }}>
                <Icon name={"camera_form_icon"} size="129px" />
              </div>

              <Styles.InputAndErrorContainer>
                <Styles.Input
                  type="text"
                  name="machineName"
                  placeholder="Camera Name"
                  {...register("machineName")}
                />
                <Styles.SubmitError>{errors.machineName?.message}</Styles.SubmitError>
              </Styles.InputAndErrorContainer>

              <Styles.InputAndErrorContainer>
                <Styles.Input
                  type="text"
                  name="machineRTSPUrl"
                  placeholder="RTSP URL"
                  {...register("machineRTSPUrl")}
                />
                <Styles.SubmitError>{errors.machineRTSPUrl?.message}</Styles.SubmitError>
              </Styles.InputAndErrorContainer>

              <Styles.InputAndErrorContainer>
                <Styles.Input
                  type="text"
                  name="machineCustomId"
                  {...register("machineCustomId")}
                  placeholder="Camera ID (optional)"
                />
                <Styles.SubmitError>{errors.machineCustomId?.message}</Styles.SubmitError>
              </Styles.InputAndErrorContainer>
            </>
          )}

          {/* Step 2: Select Use Case */}
          {currentStep === 2 && (
            <>
              <UseCaseGrid
                useCases={sortedPadmeModels}
                selectedUseCase={selectedUseCase}
                onSelect={handleUseCaseSelect}
              />
            </>
          )}

          {/* Step 3: Choose a Model */}
          {currentStep === 3 && (
            <>
              {selectedModel?.type === "object_detection" && (
                <Styles.InputAndErrorContainer>
                  <Styles.RadioContainer>
                    <ObjectSelector
                      values={watchAll.machineSelectedObjects || []}
                      onChange={handleObjectChange}
                      suggestions={objectSuggestions}
                    />
                  </Styles.RadioContainer>
                </Styles.InputAndErrorContainer>
              )}
              {watchAll.machineSelectedObjects.length > 0 && (
                <Styles.InputAndErrorContainer>
                  <Styles.RadioContainer>
                    <Styles.Label>ML Model</Styles.Label>
                    {mlModels.map((model) => {
                      return model.ModelVersions.map((modelVersion) => (
                        <Styles.RadioInputLabelContainer key={modelVersion.id}>
                          <div>
                            <Styles.RadioInput
                              type="radio"
                              id={modelVersion.id}
                              value={modelVersion.id}
                              {...register("modelVersionId")}
                            />
                          </div>

                          <Styles.RadioLabel htmlFor={modelVersion.id}>
                            {model.modelName}, Ver.{modelVersion.modelVersionNo}
                          </Styles.RadioLabel>
                        </Styles.RadioInputLabelContainer>
                      ));
                    })}
                  </Styles.RadioContainer>
                  <Styles.SubmitError>{errors.modelVersionId?.message}</Styles.SubmitError>
                </Styles.InputAndErrorContainer>
              )}
              {selectedModel?.type !== "object_detection" && (
                <Styles.InputAndErrorContainer>
                  <Styles.RadioContainer>
                    <Styles.Label>ML Model</Styles.Label>
                    {mlModels.map((model) => {
                      return model.ModelVersions.map((modelVersion) => (
                        <Styles.RadioInputLabelContainer key={modelVersion.id}>
                          <div>
                            <Styles.RadioInput
                              type="radio"
                              id={modelVersion.id}
                              value={modelVersion.id}
                              {...register("modelVersionId")}
                            />
                          </div>

                          <Styles.RadioLabel htmlFor={modelVersion.id}>
                            {model.modelName}, Ver.{modelVersion.modelVersionNo}
                          </Styles.RadioLabel>
                        </Styles.RadioInputLabelContainer>
                      ));
                    })}
                  </Styles.RadioContainer>
                  <Styles.SubmitError>{errors.modelVersionId?.message}</Styles.SubmitError>
                </Styles.InputAndErrorContainer>
              )}
              <Styles.InputAndErrorContainer>
                <Styles.RadioContainer>
                  <Styles.Label>Machine Type</Styles.Label>
                  <Styles.RadioInputLabelContainer>
                    <div>
                      <Styles.RadioInput
                        type="radio"
                        id="openvino"
                        value="openvino"
                        {...register("machineType")}
                      />
                    </div>
                    <Styles.RadioLabel htmlFor="openvino">CPU (Intel OpenVINO)</Styles.RadioLabel>
                  </Styles.RadioInputLabelContainer>

                  <Styles.RadioInputLabelContainer>
                    <div>
                      <Styles.RadioInput
                        type="radio"
                        id="cuda"
                        value="cuda"
                        {...register("machineType")}
                      />
                    </div>
                    <Styles.RadioLabel htmlFor="cuda">GPU (TensorRT)</Styles.RadioLabel>
                  </Styles.RadioInputLabelContainer>

                  <Styles.RadioInputLabelContainer>
                    <div>
                      <Styles.RadioInput
                        type="radio"
                        id="cpu"
                        value="cpu"
                        {...register("machineType")}
                      />
                    </div>
                    <Styles.RadioLabel htmlFor="cpu">CPU (Most Compatible)</Styles.RadioLabel>
                  </Styles.RadioInputLabelContainer>
                </Styles.RadioContainer>
              </Styles.InputAndErrorContainer>
              <Styles.InputAndErrorContainer>
                <Styles.RadioContainer>
                  <Styles.Label>{selectedModel.title}</Styles.Label>
                  <Styles.SliderContainer>
                    <Styles.Slider
                      defaultValue={selectedModel.defaultValue}
                      value={watchAll.machineInferenceInterval}
                      step={selectedModel.step}
                      max={selectedModel.max}
                      min={selectedModel.min}
                      onChange={(value) => {
                        setValue("machineInferenceInterval", value);
                        handleSliderChange(value);
                      }}
                      renderTrack={Track}
                      renderThumb={Thumb}
                    />
                    <Styles.SliderValue>{sliderValue}</Styles.SliderValue>
                  </Styles.SliderContainer>
                </Styles.RadioContainer>
              </Styles.InputAndErrorContainer>
              {/* <Styles.InputAndErrorContainer>
                <Styles.RadioContainer>
                  <Styles.Label>Async Inference</Styles.Label>
                  <Controller
                    name="machineAsyncInference"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <>
                        {[true, false].map((optionValue) => (
                          <Styles.RadioInputLabelContainer key={optionValue}>
                            <div>
                              <Styles.RadioInput
                                type="radio"
                                id={`asyncInferenceToggle${optionValue}`}
                                checked={value === optionValue}
                                onChange={() => onChange(optionValue)}
                              />
                            </div>
                            <Styles.RadioLabel htmlFor={`asyncInferenceToggle${optionValue}`}>
                              {optionValue ? "True" : "False"}
                            </Styles.RadioLabel>
                          </Styles.RadioInputLabelContainer>
                        ))}
                      </>
                    )}
                  />
                </Styles.RadioContainer>
              </Styles.InputAndErrorContainer> */}
              <Styles.InputAndErrorContainer>
                <Styles.RadioContainer>
                  <Styles.Label>Confidence Threshold (%)</Styles.Label>
                  <Styles.SliderContainer>
                    <Controller
                      name="machineConfidenceThreshold"
                      control={control}
                      rules={{ valueAsNumber: true }}
                      render={({ field: { onChange, value } }) => (
                        <Styles.Slider
                          value={value}
                          onChange={onChange}
                          defaultValue={50}
                          step={1}
                          max={100}
                          min={0}
                          renderTrack={Track}
                          renderThumb={Thumb}
                        />
                      )}
                    />
                    <Styles.SliderValue>{watchAll.machineConfidenceThreshold}</Styles.SliderValue>
                  </Styles.SliderContainer>
                </Styles.RadioContainer>
              </Styles.InputAndErrorContainer>
            </>
          )}
          {/* Step 4: Last Details */}
          {currentStep === 4 && (
            <>
              <Styles.InputAndErrorContainer>
                <Styles.RadioContainer>
                  <Styles.Label>Docker ID</Styles.Label>

                  {dockersList.map((docker) => (
                    <Styles.RadioInputLabelContainer key={docker.id}>
                      <Styles.RadioInput
                        type="radio"
                        id={docker.id}
                        value={docker.id}
                        {...register("dockerId")}
                      />
                      <Styles.RadioLabel htmlFor={docker.id}>{docker.dockerName}</Styles.RadioLabel>
                    </Styles.RadioInputLabelContainer>
                  ))}
                </Styles.RadioContainer>

                <Styles.SubmitError>{errors.dockerId?.message}</Styles.SubmitError>
              </Styles.InputAndErrorContainer>

              <Styles.InputAndErrorContainer>
                <Styles.RadioContainer>
                  <Styles.Label>Area</Styles.Label>

                  {venueAreas.map((area) => (
                    <Styles.RadioInputLabelContainer key={area.id}>
                      <div>
                        <Styles.RadioInput
                          type="radio"
                          id={`area-${area.id}`}
                          value={area.id}
                          {...register("areaId")}
                          onChange={handleAreaOnChange}
                        />
                      </div>
                      <Styles.RadioLabel htmlFor={`area-${area.id}`}>
                        {area.areaName}
                      </Styles.RadioLabel>
                    </Styles.RadioInputLabelContainer>
                  ))}

                  <Styles.RadioInputLabelContainer>
                    <Icon
                      name={"add_area"}
                      className="action-icon"
                      onClick={() => {
                        setAddNewArea(true);
                        setValue("areaId", "");
                      }}
                    />
                    <Styles.RadioLabel>Add area</Styles.RadioLabel>
                  </Styles.RadioInputLabelContainer>
                </Styles.RadioContainer>
                <Styles.SubmitError>{errors.areaId?.message}</Styles.SubmitError>
              </Styles.InputAndErrorContainer>

              {addNewArea && (
                <Styles.InputAndErrorContainer>
                  <Styles.Input
                    type="text"
                    name="newArea"
                    placeholder="New area name"
                    {...register("newArea")}
                  />
                  <Styles.SubmitError>{errors.newArea?.message}</Styles.SubmitError>
                </Styles.InputAndErrorContainer>
              )}

              <Styles.InputAndErrorContainer>
                <Styles.Input
                  type="text"
                  name="machineOverlayLabel"
                  {...register("machineOverlayLabel")}
                  placeholder="Overlay label"
                />
                <Styles.SubmitError>{errors.machineOverlayLabel?.message}</Styles.SubmitError>
              </Styles.InputAndErrorContainer>

              <Styles.InputAndErrorContainer>
                <Styles.RadioContainer>
                  <Styles.Label>Anti-Lag</Styles.Label>
                  <Controller
                    name="machineAntiLag"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <>
                        {[true, false].map((optionValue) => (
                          <Styles.RadioInputLabelContainer key={optionValue}>
                            <div>
                              <Styles.RadioInput
                                type="radio"
                                id={`antiLagToggle${optionValue}`}
                                checked={value === optionValue}
                                onChange={() => onChange(optionValue)}
                              />
                            </div>
                            <Styles.RadioLabel htmlFor={`antiLagToggle${optionValue}`}>
                              {optionValue ? "True" : "False"}
                            </Styles.RadioLabel>
                          </Styles.RadioInputLabelContainer>
                        ))}
                      </>
                    )}
                  />
                </Styles.RadioContainer>
              </Styles.InputAndErrorContainer>

              <Styles.InputAndErrorContainer>
                <Styles.RadioContainer>
                  <Styles.Label>Privacy Beta</Styles.Label>
                  <Controller
                    name="machinePrivacy"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <>
                        {[true, false].map((optionValue) => (
                          <Styles.RadioInputLabelContainer key={optionValue}>
                            <div>
                              <Styles.RadioInput
                                type="radio"
                                id={`privacyBetaToggle${optionValue}`}
                                checked={value === optionValue}
                                onChange={() => onChange(optionValue)}
                              />
                            </div>
                            <Styles.RadioLabel htmlFor={`privacyBetaToggle${optionValue}`}>
                              {optionValue ? "True" : "False"}
                            </Styles.RadioLabel>
                          </Styles.RadioInputLabelContainer>
                        ))}
                      </>
                    )}
                  />
                </Styles.RadioContainer>
              </Styles.InputAndErrorContainer>
              <Styles.InputAndErrorContainer>
                <Styles.InputContainer>
                  <Styles.Label>Reset Interval (seconds)</Styles.Label>
                  <Styles.InputInContainer
                    type="text"
                    name="machineResetInterval"
                    {...register("machineResetInterval")}
                    // placeholder="Reset Interval (seconds)"
                  />
                  <Styles.SubmitError>{errors.machineResetInterval?.message}</Styles.SubmitError>
                </Styles.InputContainer>
              </Styles.InputAndErrorContainer>
            </>
          )}
          <Styles.ButtonsContainer>
            {currentStep === 1 ? (
              <PrimaryBtn
                type="button"
                id="continue"
                label="Continue"
                width="420px"
                height="40px"
                handleClick={handleNextStep}
              />
            ) : (
              <Styles.SmallButtonsContainer>
                <SecondaryBtn
                  label="Previous"
                  handleClick={handlePrevStep}
                  width="200px"
                  height="40px"
                />
                <PrimaryBtn
                  type="button"
                  id="submit"
                  label={currentStep === 4 ? (isUpdate ? "Update" : "Add") : "Next"}
                  width="200px"
                  height="40px"
                  handleClick={currentStep === 4 ? handleSubmit(submitForm) : handleNextStep}
                />
              </Styles.SmallButtonsContainer>
            )}
          </Styles.ButtonsContainer>
        </Styles.CameraFormContainer>
      </Styles.Form>
    </ModalWithChildren>
  );
};

export default AddUpdateCamera;
